import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as emailVendorServices from "../../../services/emailVendors";

export const fetchEmailVendors = createAsyncThunk(
  "emailVendors/fetch",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await emailVendorServices.fetchEmailVendors(payload);
      if (!res.success) return rejectWithValue(res.message);
      return res.data; // {records, total, page, limit, totalPages}
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch email vendors");
    }
  }
);

export const createEmailVendor = createAsyncThunk(
  "emailVendors/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await emailVendorServices.createEmailVendor(payload);
      if (!res.success) return rejectWithValue(res.message);
      return res;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to create email vendor");
    }
  }
);

export const updateEmailVendor = createAsyncThunk(
  "emailVendors/update",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await emailVendorServices.updateEmailVendor(payload);
      if (!res.success) return rejectWithValue(res.message);
      return res;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to update email vendor");
    }
  }
);

export const testEmailVendor = createAsyncThunk(
  "emailVendors/test",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await emailVendorServices.testEmailVendor(payload);
      if (!res.success) return rejectWithValue(res.message);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to test email vendor");
    }
  }
);

const initialState = {
  list: [],
  pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
  loading: false,
  error: null,
  testResult: null,
};

const emailVendorsSlice = createSlice({
  name: "emailVendors",
  initialState,
  reducers: {
    clearEmailVendorError(state) {
      state.error = null;
    },
    clearEmailVendorTestResult(state) {
      state.testResult = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEmailVendors.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEmailVendors.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload.records || [];
      state.pagination = {
        total: action.payload.total,
        page: action.payload.page,
        limit: action.payload.limit,
        totalPages: action.payload.totalPages,
      };
    });
    builder.addCase(fetchEmailVendors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to fetch email vendors";
      state.list = [];
    });

    builder.addCase(createEmailVendor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createEmailVendor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createEmailVendor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to create email vendor";
    });

    builder.addCase(updateEmailVendor.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateEmailVendor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateEmailVendor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to update email vendor";
    });

    builder.addCase(testEmailVendor.pending, (state) => {
      state.testResult = null;
    });
    builder.addCase(testEmailVendor.fulfilled, (state, action) => {
      state.testResult = action.payload;
    });
    builder.addCase(testEmailVendor.rejected, (state, action) => {
      state.testResult = { ok: false, error: action.payload };
    });
  },
});

export const { clearEmailVendorError, clearEmailVendorTestResult } =
  emailVendorsSlice.actions;
export const emailVendorsReducer = emailVendorsSlice.reducer;
